import { useEffect } from "react";
import { useGeneralContext } from "../context/AppGeneralContext";
import { useRTContext } from "../context/RTContext";
import { useTestCreationContext } from "../context/TestCreationContext";
import { getTestBook, getTrainingBook } from "../helpers/BookAvailability";
import { hasTestVocabulary, hasTrainingCards } from "../helpers/ClassActivityMapping";
import { useActivityBookAvailability } from "./useActivityBookAvailability";

/**
 * Probe for the existing-activity source: the two list requests plus the band infos of the books
 * they reference, so the source is only offered when something in it is actually selectable.
 */
export const useHasExistingActivities = () => {
    const { userId, userInfo } = useGeneralContext();
    const { userTests, dataLoaded: testsLoaded, loadUserTests } = useTestCreationContext();
    const { vocabTrainings, vocabTrainingsDataLoaded, fetchAndStoreVocabTrainings } = useRTContext();

    const isTeacher = userInfo.visitorOrUserRole?.toLowerCase() === "teacher";
    const areListsLoaded = testsLoaded && vocabTrainingsDataLoaded;

    const testsWithVocabulary = userTests.filter(hasTestVocabulary);
    const trainingsWithCards = vocabTrainings.filter(hasTrainingCards);

    const { areBookInfosResolved, isBookOfActivityBlocked } = useActivityBookAvailability(
        testsWithVocabulary,
        trainingsWithCards,
        isTeacher && areListsLoaded
    );

    useEffect(() => {
        if (!userId || !isTeacher) return;
        if (!testsLoaded) loadUserTests();
        if (!vocabTrainingsDataLoaded) fetchAndStoreVocabTrainings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, isTeacher]);

    if (!isTeacher) return { hasActivities: false };
    if (!areListsLoaded || !areBookInfosResolved) return { hasActivities: undefined };

    const hasActivities =
        testsWithVocabulary.some((test) => !isBookOfActivityBlocked(getTestBook(test)?.id)) ||
        trainingsWithCards.some((training) => !isBookOfActivityBlocked(getTrainingBook(training, userId)?.id));
    return { hasActivities };
};
